import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { faUserPlus } from "@fortawesome/pro-regular-svg-icons";
import { toast } from "react-toastify";
import { openModal } from "../../store/modal/modalReducer";
import NotificationToast from "../Helpers/NotificationToast";
import CustomButton from "../Helpers/CustomButton";
import moment from "moment/moment";
import ScheduledPayments from "./ScheduledPayments";

export default function GridPayments({ setDoc, db }) {
    const [loading, setLoading] = useState(true);
    const { current_staff, permissions } = useSelector((state) => state.auth);
    const { current_booking } = useSelector((state) => state.booking);
    const [banks, setBanks] = useState([]);
    const [allPayments, setAllPayments] = useState([]);
    const [fetchPayments, setFetchPayments] = useState(true);
    const dispatch = useDispatch();
    const [paymentFiles, setPaymentFiles] = useState([]);
    const [refunds, setRefunds] = useState([]);
    const [pendingBatchCount, setPendingBatchCount] = useState(0);
    let paymentModules = [
        { label: "Refund", value: "Refund" },
        { label: "Booking Fee", value: "Booking Fee" },
        { label: "WiFi", value: "WiFi" },
        { label: "Building", value: "Building" },
        { label: "Guest", value: "Guest" },
        { label: "RAA", value: "RAA" },
        { label: "Monthly Rent", value: "Monthly Rent" },
        { label: "Storage Fee", value: "Storage Fee" },
        { label: "Extension", value: "Extension" },
    ];
    if (process.env.REACT_APP_COLOUR === "QL") {
        paymentModules = [
            { label: "Booking Fee", value: "Booking Fee" },
            { label: "WiFi", value: "WiFi" },
            { label: "Monthly Rent", value: "Monthly Rent" },
        ];
    }
    const paymentVendors = [
        { label: "Ozow", value: 1 },
        { label: "PayFast", value: 2 },
        { label: "Peach", value: 3 },
    ];
    const [showSendPayment, setShowSendPayment] = useState(false);
    const [selectedPaymentModule, setSelectedPaymentModule] = useState(null);
    const [selectedPaymentVendor, setSelectedPaymentVendor] = useState(null);
    const [amountToPay, setAmountToPay] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showBankingForm, setShowBankingForm] = useState(false);
    const [bankingFormData, setBankingFormData] = useState({
        banking_bank: "",
        banking_account_type: "",
        banking_branch: "",
        banking_branch_code: "",
        banking_bank_code: "",
        banking_account_number: "",
        banking_account_holder: "",
    });

    // Separate payments into refunds and other payments
    const refundPayments = allPayments?.filter((payment) => payment.payment_module === "Refund") || [];
    const otherPayments = allPayments?.filter((payment) => payment.payment_module !== "Refund") || [];

    useEffect(() => {
        if (banks.length === 0) {
            axios({
                method: "get",
                url: `${process.env.REACT_APP_API_URL}paystack_banks`,
            }).then((response) => {
                let bank_data = response.data;
                if (bank_data.status === true) {
                    setBanks(bank_data.data);
                }
            });
        }

        if (fetchPayments === true) {
            setFetchPayments(false);
            axios({
                method: "get",
                url: `${process.env.REACT_APP_API_URL}get_payments/${current_booking?.user.id}`,
            }).then((response) => {
                setAllPayments(response.data);
            });
        }

        if (refunds.length === 0) {
            axios({
                method: "get",
                url: `${process.env.REACT_APP_API_URL}get_refunds/${current_booking?.user.id}`,
            }).then((response) => {
                setRefunds(response.data);
            });
        }

        // Fetch pending batch count
        if (current_staff?.id) {
            axios({
                method: "get",
                url: `${process.env.REACT_APP_API_URL}payments/batch/pending`,
                params: { staff_id: current_staff.id },
            })
                .then((response) => {
                    if (response.data.success) {
                        setPendingBatchCount(response.data.count || 0);
                    }
                })
                .catch(() => {
                    // Silently fail - user might not have permission
                    setPendingBatchCount(0);
                });
        }
    }, [fetchPayments, current_booking, current_staff]);

    const handleDeletePayment = async (paymentId) => {
        if (!window.confirm("Are you sure you want to delete this payment record?")) {
            return;
        }

        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}payments/${paymentId}`);
            toast(<NotificationToast title="Success" message="Payment deleted successfully" type="success" />);
            setFetchPayments(true);
        } catch (error) {
            toast(<NotificationToast title="Error" message="Failed to delete payment" type="error" />);
        }
    };

    const PaymentCard = ({ payment }) => {
        // Safely parse transaction_result
        let transactionData = null;
        try {
            if (payment.transaction_result) {
                transactionData = JSON.parse(payment.transaction_result);
            }
        } catch (e) {
            console.error("Failed to parse transaction_result:", e);
        }

        return (
            <div className="bg-white shadow-sm rounded-lg p-4 mb-4 border border-gray-200 hover:shadow-md transition-all duration-200">
                <div className="flex flex-col space-y-2">
                    {/* Test Payment Indicator */}
                    {payment.is_test && (
                        <div className="flex items-center justify-center">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">TEST PAYMENT</span>
                        </div>
                    )}
                    {/* For Peach payments, use the status field directly */}
                    {payment.payment_provider === "peach" ? (
                        payment.status === "Complete" ? (
                            <div className="flex items-center justify-center bg-green-50 py-2 rounded-md">
                                <span className="text-green-600 font-medium">Payment Successful</span>
                            </div>
                        ) : payment.status === "Pending" ? (
                            <div className="flex items-center justify-center bg-blue-50 py-2 rounded-md">
                                <span className="text-blue-600 font-medium">Payment Pending</span>
                            </div>
                        ) : payment.status === "Failed" ? (
                            <div className="flex items-center justify-center bg-red-50 py-2 rounded-md">
                                <span className="text-red-600 font-medium">Payment Failed</span>
                            </div>
                        ) : (
                            <div className="flex items-center justify-center bg-orange-50 py-2 rounded-md">
                                <span className="text-orange-600 font-medium">{payment.status}</span>
                            </div>
                        )
                    ) : transactionData ? (
                        transactionData.status === true || transactionData.Status === "Complete" || transactionData.payment_status === "COMPLETE" ? (
                            <div className="flex items-center justify-center bg-green-50 py-2 rounded-md">
                                <span className="text-green-600 font-medium">Payment Successful</span>
                            </div>
                        ) : (
                            <div className="flex items-center justify-center bg-red-50 py-2 rounded-md">
                                <span className="text-red-600 font-medium">Payment Failed - {transactionData.Status || transactionData.message || "Unknown Error"}</span>
                            </div>
                        )
                    ) : payment.status !== null ? (
                        <div className={`flex items-center justify-center py-2 rounded-md ${payment.status === "pending_batch" ? "bg-purple-50" : "bg-orange-50"}`}>
                            <span className={`font-medium ${payment.status === "pending_batch" ? "text-purple-600" : "text-orange-600"}`}>
                                {payment.status === "pending_batch" ? "Pending Batch Refund" : payment.status}
                            </span>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center bg-red-50 py-2 rounded-md">
                            <span className="text-red-600 font-medium">Payment Failed - Payment Abandoned</span>
                        </div>
                    )}
                    <div className="text-center space-y-1">
                        <p className="text-gray-600">{moment(payment.created_at).format("DD MMMM yyyy HH:mm")}</p>
                        <p className="text-gray-800">{payment.name}</p>
                        <p className="text-lg font-bold text-primary">R {(parseFloat(payment.amount) / 100).toFixed(2)}</p>
                        <p className="text-gray-700">Reason: {payment.refund_reason || payment.reason}</p>
                        <p className="text-gray-700">Module: {payment.payment_module}</p>
                        {payment.booking_charge_id > 0 && <p className="text-gray-700">GTE: {payment.booking_charge?.status === 1 ? "Processed" : "Pending"}</p>}
                        {payment.booking_charge_id > 0 && payment.booking_charge?.status === 1 && <p className="text-gray-700">Batch: {payment.booking_charge?.batch_no}</p>}
                    </div>
                    {/* Delete button - only for roger@qholdings.co.za */}
                    {current_staff?.email === "roger@qholdings.co.za" && (
                        <div className="flex justify-center pt-2">
                            <button onClick={() => handleDeletePayment(payment.id)} className="px-3 py-1 bg-red-600 text-white text-xs rounded-md hover:bg-red-700 transition-colors">
                                Delete
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const BankingInfo = () => (
        <div className="bg-white shadow-sm rounded-lg p-4 mb-6 border border-gray-200">
            <div className="flex justify-between items-center mb-3">
                <h3 className="text-base font-semibold">Banking Information</h3>
                {/* Only show edit button for authorized staff */}
                {(current_staff?.email === "roger@qholdings.co.za" ||
                    current_staff?.email === "sarel@qholdings.co.za" ||
                    current_staff?.email === "johann@qholdings.co.za" ||
                    current_staff?.email === "chantal@qholdings.co.za") && (
                    <button
                        onClick={() => {
                            // Pre-populate form with existing data
                            setBankingFormData({
                                banking_bank: current_booking?.user?.profile?.banking_bank || "",
                                banking_account_type: current_booking?.user?.profile?.banking_account_type || "",
                                banking_branch: current_booking?.user?.profile?.banking_branch || "",
                                banking_branch_code: current_booking?.user?.profile?.banking_branch_code || "",
                                banking_bank_code: current_booking?.user?.profile?.banking_bank_code || "",
                                banking_account_number: current_booking?.user?.profile?.banking_account_number || "",
                                banking_account_holder: current_booking?.user?.profile?.banking_account_holder || "",
                            });
                            setShowBankingForm(true);
                        }}
                        className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700"
                    >
                        Edit
                    </button>
                )}
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                    <p className="text-gray-600 text-xs">Bank</p>
                    <p className="font-medium break-words">{current_booking?.user?.profile?.banking_bank}</p>
                </div>
                <div>
                    <p className="text-gray-600 text-xs">Account Type</p>
                    <p className="font-medium">{current_booking?.user?.profile?.banking_account_type}</p>
                </div>
                <div>
                    <p className="text-gray-600 text-xs">Branch</p>
                    <p className="font-medium break-words">{current_booking?.user?.profile?.banking_branch}</p>
                </div>
                <div>
                    <p className="text-gray-600 text-xs">Branch Code</p>
                    <p className="font-medium">{current_booking?.user?.profile?.banking_branch_code}</p>
                </div>
                <div>
                    <p className="text-gray-600 text-xs">Account Number</p>
                    <p className="font-medium break-words">{current_booking?.user?.profile?.banking_account_number}</p>
                </div>
                <div>
                    <p className="text-gray-600 text-xs">Account Holder</p>
                    <p className="font-medium break-words">{current_booking?.user?.profile?.banking_account_holder}</p>
                </div>
            </div>
        </div>
    );

    async function new_recipient() {
        // Open modal directly - provider selection happens in modal
        // Recipient creation will happen on submit based on selected provider
        dispatch(openModal({ modal_type: "ModalSinglePayment", modal_props: { setFetchPayments: setFetchPayments } }));
    }

    const handleBankingFormChange = (e) => {
        setBankingFormData({
            ...bankingFormData,
            [e.target.name]: e.target.value,
        });
    };

    const handleBankingFormSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            await axios.post(`${process.env.REACT_APP_API_URL}user_banking_details`, {
                booking_id: current_booking.id,
                staff_id: current_staff.id,
                ...bankingFormData,
            });

            toast.success("Banking details updated successfully");
            setShowBankingForm(false);
            window.location.reload(); // Refresh to show updated banking info
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to update banking details");
        } finally {
            setIsSubmitting(false);
        }
    };

    async function new_recipient_for_batch() {
        if (current_booking?.user.recipient_code === null) {
            await axios({
                method: "post",
                url: `${process.env.REACT_APP_API_URL}create_recipient`,
                data: { booking_id: current_booking.id },
            })
                .then((response) => {
                    if (response.data[0] === "PAYMENT FAILED") {
                        toast(<NotificationToast title={"Recipient Failed"} message={response.data[1]} list={true} type="error" />);
                    } else {
                        dispatch(
                            openModal({
                                modal_type: "ModalQueueRefund",
                                modal_props: {
                                    booking_id: current_booking.id,
                                    onSuccess: () => setFetchPayments(true),
                                },
                            }),
                        );
                    }
                })
                .catch((error) => {
                    toast.error("Recipient creation failed");
                });
        } else {
            dispatch(
                openModal({
                    modal_type: "ModalQueueRefund",
                    modal_props: {
                        booking_id: current_booking.id,
                        onSuccess: () => setFetchPayments(true),
                    },
                }),
            );
        }
    }

    const submitPayment = () => {
        if (!selectedPaymentModule || !selectedPaymentVendor || !amountToPay) {
            alert("Please fill out all fields before submitting.");
            return;
        }

        setIsSubmitting(true);

        axios({
            method: "post",
            url: `${process.env.REACT_APP_API_URL}send_payment`,
            data: {
                payment_module: selectedPaymentModule,
                payment_vendor: selectedPaymentVendor,
                amount: amountToPay,
                booking_id: current_booking.id,
                user_id: current_booking?.user?.id,
            },
        })
            .then((response) => {
                if (response.data.message === "done") {
                    setShowSendPayment(false);
                    setSelectedPaymentModule(null);
                    setSelectedPaymentVendor(null);
                    setAmountToPay(null);
                    setFetchPayments(true);
                    toast.success("Payment submitted successfully!");
                }
            })
            .catch((error) => {
                console.error("Payment Submission Failed", error);
                toast.error("Payment submission failed. Please try again.");
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <div className="h-96 overflow-y-auto scroll-smooth px-4">
            <div className="space-y-6">
                {/* Refunds & Banking Section */}
                <section>
                    {/* Banking Information or Form */}
                    {showBankingForm ? (
                        <div className="bg-white shadow-sm rounded-lg p-6 mb-6 border border-gray-200">
                            <h3 className="text-lg font-semibold mb-4">{current_booking?.user?.profile?.banking_account_number ? "Edit" : "Add"} Banking Details</h3>
                            <form onSubmit={handleBankingFormSubmit}>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name *</label>
                                        <select
                                            name="banking_bank"
                                            value={bankingFormData.banking_bank}
                                            onChange={(e) => {
                                                // Auto-fill bank code and branch code when bank is selected
                                                const selectedBank = banks.find((b) => b.name === e.target.value);
                                                if (selectedBank) {
                                                    setBankingFormData((prev) => ({
                                                        ...prev,
                                                        banking_bank: e.target.value,
                                                        banking_bank_code: selectedBank.code,
                                                        banking_branch_code: selectedBank.code, // Use bank code as branch code
                                                        banking_branch: selectedBank.name, // Use bank name as branch
                                                    }));
                                                } else {
                                                    handleBankingFormChange(e);
                                                }
                                            }}
                                            required
                                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        >
                                            <option value="">Select Bank</option>
                                            {banks.map((bank, index) => (
                                                <option key={index} value={bank.name}>
                                                    {bank.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Account Type *</label>
                                        <select
                                            name="banking_account_type"
                                            value={bankingFormData.banking_account_type}
                                            onChange={handleBankingFormChange}
                                            required
                                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        >
                                            <option value="">Select Type</option>
                                            <option value="Cheque">Cheque</option>
                                            <option value="Savings">Savings</option>
                                            <option value="Current">Current</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Account Number *</label>
                                        <input
                                            type="text"
                                            name="banking_account_number"
                                            value={bankingFormData.banking_account_number}
                                            onChange={handleBankingFormChange}
                                            required
                                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Account Holder Name</label>
                                        <input
                                            type="text"
                                            name="banking_account_holder"
                                            value={bankingFormData.banking_account_holder}
                                            onChange={handleBankingFormChange}
                                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>
                                <div className="mt-4 flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowBankingForm(false)}
                                        disabled={isSubmitting}
                                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                                        {isSubmitting ? "Saving..." : "Save Banking Details"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    ) : current_booking?.user?.profile?.banking_account_number ? (
                        <BankingInfo />
                    ) : (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6 text-center">
                            <p className="text-gray-700 mb-3">Bank Information Not Loaded</p>
                            {/* Only show add button for authorized staff */}
                            {(current_staff?.email === "roger@qholdings.co.za" ||
                                current_staff?.email === "sarel@qholdings.co.za" ||
                                current_staff?.email === "johann@qholdings.co.za" ||
                                current_staff?.email === "chantal@qholdings.co.za") && (
                                <CustomButton text="Add Banking Details" styling="bg-blue-600 hover:bg-blue-700" icon={faUserPlus} onClick={() => setShowBankingForm(true)} />
                            )}
                        </div>
                    )}

                    {!showBankingForm && (
                        <>
                            <h4 className="text-md font-medium mb-3">Refund Payments</h4>

                            {/* Send Refund Button */}
                            {((process.env.REACT_APP_COLOUR === "UC" && (current_staff.id == "291" || current_staff.id == "1" || current_staff.id == "14102")) ||
                                (process.env.REACT_APP_COLOUR === "QL" &&
                                    (current_staff.id == "294" || current_staff.id == "297" || current_staff.id == "291" || current_staff.id == "8684" || current_staff.id == "1"))) && (
                                <CustomButton text={"Send Refund"} styling="w-full mt-2" icon={faUserPlus} onClick={() => new_recipient()} loadingtext="Loading..." />
                            )}

                            {/* Batch Queue Buttons */}
                            {(permissions?.includes('receive_refund_otp') || permissions?.includes('view_batch_refunds')) && (
                                <>
                                    <CustomButton
                                        text={"Queue Refund (Batch)"}
                                        styling="w-full mt-2 bg-blue-600 hover:bg-blue-700"
                                        icon={faUserPlus}
                                        onClick={() => new_recipient_for_batch()}
                                        loadingtext="Loading..."
                                    />
                                    <div className="relative">
                                        <CustomButton
                                            text={`View Batch Queue ${pendingBatchCount > 0 ? `(${pendingBatchCount})` : ""}`}
                                            styling="w-full mt-2 bg-green-600 hover:bg-green-700"
                                            icon={faUserPlus}
                                            onClick={() =>
                                                dispatch(
                                                    openModal({
                                                        modal_type: "ModalBatchRefunds",
                                                        modal_props: {},
                                                    }),
                                                )
                                            }
                                            loadingtext="Loading..."
                                        />
                                    </div>
                                </>
                            )}

                            {/* Refund Payments */}
                            {refundPayments.length > 0 && (
                                <div className="my-6">
                                    {refundPayments.map((payment, index) => (
                                        <PaymentCard key={index} payment={payment} />
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </section>

                {/* Other Payments Section */}
                <section>
                    <h3 className="text-lg font-semibold mb-4">Incoming Payments</h3>

                    {/* Payment Form */}
                    {(process.env.REACT_APP_COLOUR === "UC" || process.env.REACT_APP_COLOUR === "QL" || process.env.REACT_APP_COLOUR === "QP") && (
                        <div className="mb-6">
                            <button
                                className="bg-primary w-full text-center text-white uppercase px-4 py-2 rounded-lg text-sm focus:z-10 focus:ring-2 focus:ring-offset-2 transition duration-150 ease-in-ease-out hover:-translate-y-0.5"
                                onClick={() => setShowSendPayment(!showSendPayment)}
                            >
                                {showSendPayment ? "Hide Payment Form" : "Request Payment"}
                            </button>

                            {showSendPayment && (
                                <div className="bg-white shadow-sm rounded-lg p-6 border border-gray-200">
                                    <div className="space-y-4">
                                        <div>
                                            <label htmlFor="paymentModule" className="block text-sm font-medium text-gray-700 mb-1">
                                                Payment Module
                                            </label>
                                            <select
                                                id="paymentModule"
                                                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                                                value={selectedPaymentModule || ""}
                                                onChange={(e) => setSelectedPaymentModule(e.target.value)}
                                            >
                                                <option value="" disabled>
                                                    Select a payment module
                                                </option>
                                                {paymentModules.map((module) => (
                                                    <option key={module.value} value={module.value}>
                                                        {module.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label htmlFor="paymentVendor" className="block text-sm font-medium text-gray-700 mb-1">
                                                Payment Vendor
                                            </label>
                                            <select
                                                id="paymentVendor"
                                                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                                                value={selectedPaymentVendor || ""}
                                                onChange={(e) => setSelectedPaymentVendor(e.target.value)}
                                            >
                                                <option value="" disabled>
                                                    Select a payment vendor
                                                </option>
                                                {paymentVendors.map((vendor) => (
                                                    <option key={vendor.value} value={vendor.value}>
                                                        {vendor.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label htmlFor="amountToPay" className="block text-sm font-medium text-gray-700 mb-1">
                                                Amount to Pay
                                            </label>
                                            <input
                                                type="text"
                                                id="amountToPay"
                                                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                                                placeholder="Enter amount"
                                                value={amountToPay || ""}
                                                onChange={(e) => setAmountToPay(e.target.value)}
                                            />
                                        </div>

                                        {/* Test Card Information for Payment Staging Mode */}
                                        {current_booking?.payment_staging === 1 && selectedPaymentVendor === "3" && (
                                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                                <h4 className="text-sm font-semibold text-yellow-800 mb-2">Test Card Details (Staging Mode)</h4>
                                                <div className="text-sm text-yellow-700 space-y-1">
                                                    <p>
                                                        <span className="font-medium">Card number:</span> 5200000000000023
                                                    </p>
                                                    <p>
                                                        <span className="font-medium">CVV:</span> Any three digits
                                                    </p>
                                                    <p>
                                                        <span className="font-medium">Expiry date:</span> Any future date
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        <button
                                            className="w-full bg-primary text-white py-2 rounded-lg font-medium hover:bg-primary_hover transition-all duration-200 disabled:opacity-50"
                                            onClick={submitPayment}
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? "Submitting..." : "Submit Payment"}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Other Payments List */}
                    {otherPayments.map((payment, index) => (
                        <PaymentCard key={index} payment={payment} />
                    ))}
                </section>

                {/* Scheduled Payments */}
                <section className="mt-6">
                    <ScheduledPayments />
                </section>

                {/* Internal Refund Checklist Section - Moved to bottom */}
                <section className="mt-8">
                    <h3 className="text-lg font-semibold mb-4">Internal Refund Checklist</h3>

                    <button
                        className="w-full bg-primary text-white py-3 rounded-lg font-medium hover:bg-primary_hover transition-all duration-200 mb-6 text-sm"
                        onClick={() =>
                            dispatch(
                                openModal({
                                    modal_type: "ModalRefund",
                                    modal_props: { current_booking },
                                }),
                            )
                        }
                    >
                        Create Internal Refund Checklist
                    </button>

                    {/* Refund Checklists */}
                    <div className="mb-6">
                        <h4 className="text-md font-medium mb-3">Existing Checklists</h4>
                        {refunds?.map((refund, index) => (
                            <div
                                key={index}
                                className="bg-white shadow-sm rounded-lg p-4 mb-3 border border-gray-200 cursor-pointer hover:shadow-md transition-all duration-200"
                                onClick={() =>
                                    dispatch(
                                        openModal({
                                            modal_type: "ModalViewFiles",
                                            modal_props: {
                                                files: [{ id: refund.file_id }],
                                            },
                                        }),
                                    )
                                }
                            >
                                <p className="text-gray-600">{moment(refund.created_at).format("yyyy-MM-DD HH:mm")}</p>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
}
